/**
 * SameHeight Utility Class
 * 
 * A reusable class that ensures elements with the same height group
 * have equal heights. Elements are grouped using data-same-height attributes.
 * 
 * Usage:
 * 1. Add class "same-height" to container element
 * 2. Add data-same-height="index" to child elements you want to have the same height
 * 3. Optionally add same-height-device="d,m,t" to control which devices apply equal heights
 * 4. Initialize: const sameHeight = new SameHeight();
 * 5. The class automatically handles window resize events
 * 
 * Example HTML:
 * <div class="same-height">
 *   <div data-same-height="1">Card 1</div>
 *   <div data-same-height="1">Card 2 with more content</div>
 *   <div data-same-height="1">Card 3</div>
 * </div>
 * 
 * Device-specific usage:
 * <div class="same-height">
 *   <div data-same-height="1" same-height-device="d,t">Desktop & Tablet only</div>
 *   <div data-same-height="1" same-height-device="m">Mobile only</div>
 *   <div data-same-height="1" same-height-device="d">Desktop only</div>
 * </div>
 * 
 * Device codes: d=desktop, m=mobile, t=tablet
 * 
 * Advanced usage with multiple groups:
 * <div class="same-height">
 *   <div data-same-height="1">Group 1 Item A</div>
 *   <div data-same-height="1">Group 1 Item B</div>
 *   <div data-same-height="2">Group 2 Item X</div>
 *   <div data-same-height="2">Group 2 Item Y</div>
 * </div>
 */
class SameHeight {
  constructor(options = {}) {
    this.options = {
      // Debounce delay for resize events (ms)
      debounceDelay: 250,
      // Selector for container elements with same-height class
      containerSelector: '.same-height',
      // Selector for elements with same-height attribute within containers
      elementSelector: '[data-same-height]',
      // Minimum height to enforce (optional)
      minHeight: null,
      // Whether to automatically initialize on construction
      autoInit: true,
      // Device breakpoints (can be customized)
      breakpoints: {
        mobile: 768,    // Below this width = mobile
        tablet: 1024    // Between mobile and this = tablet, above = desktop
      },
      ...options
    };

    this.resizeTimeout = null;
    this.heightGroups = new Map();
    this.isInitialized = false;
    this.currentDevice = this.getCurrentDevice();

    // Bind methods to maintain context
    this.handleResize = this.handleResize.bind(this);
    this.updateHeights = this.updateHeights.bind(this);
    this.getCurrentDevice = this.getCurrentDevice.bind(this);
    this.isDeviceAllowed = this.isDeviceAllowed.bind(this);

    if (this.options.autoInit) {
      this.init();
    }
  }

  /**
   * Initialize the SameHeight utility
   */
  init() {
    if (this.isInitialized) {
      return;
    }

    this.collectElements();
    this.updateHeights();
    this.bindEvents();
    this.isInitialized = true;
  }

  /**
   * Get the current device type based on screen width
   * @returns {string} 'd' for desktop, 't' for tablet, 'm' for mobile
   */
  getCurrentDevice() {
    const width = window.innerWidth;
    const { mobile, tablet } = this.options.breakpoints;

    if (width < mobile) {
      return 'm'; // mobile
    } else if (width < tablet) {
      return 't'; // tablet
    } else {
      return 'd'; // desktop
    }
  }

  /**
   * Check if the current device is allowed for a given element
   * @param {HTMLElement} element - The element to check
   * @returns {boolean} Whether the current device is allowed
   */
  isDeviceAllowed(element) {
    const deviceAttr = element.getAttribute('same-height-device');
    
    // If no device attribute, allow all devices
    if (!deviceAttr) {
      return true;
    }

    // Parse the device string (e.g., "d,m,t" or "d" or "m,t")
    const allowedDevices = deviceAttr
      .toLowerCase()
      .split(',')
      .map(device => device.trim())
      .filter(device => ['d', 'm', 't'].includes(device));

    // Check if current device is in the allowed list
    return allowedDevices.includes(this.currentDevice);
  }

  /**
   * Collect all elements with data-same-height attributes and group them
   */
  collectElements() {
    this.heightGroups.clear();
    const containers = document.querySelectorAll(this.options.containerSelector);

    containers.forEach(container => {
      const elements = container.querySelectorAll(this.options.elementSelector);
      
      elements.forEach(element => {
        const groupName = element.getAttribute('data-same-height');
        if (!groupName) return;

        // Check if this element is allowed for the current device
        if (!this.isDeviceAllowed(element)) {
          // Reset height to auto for elements not allowed on current device
          element.style.height = 'auto';
          return;
        }

        if (!this.heightGroups.has(groupName)) {
          this.heightGroups.set(groupName, []);
        }
        this.heightGroups.get(groupName).push(element);
      });
    });
  }

  /**
   * Calculate and apply the same height to all groups
   */
  updateHeights() {
    this.heightGroups.forEach((elements, groupName) => {
      if (elements.length <= 1) return;

      // Reset heights to auto to get natural heights
      elements.forEach(element => {
        element.style.height = 'auto';
      });

      // Calculate the maximum height in the group
      let maxHeight = 0;
      elements.forEach(element => {
        const elementHeight = element.offsetHeight;
        maxHeight = Math.max(maxHeight, elementHeight);
      });

      // Apply minimum height if specified
      if (this.options.minHeight && maxHeight < this.options.minHeight) {
        maxHeight = this.options.minHeight;
      }

      // Apply the maximum height to all elements in the group
      elements.forEach(element => {
        element.style.height = `${maxHeight}px`;
      });
    });
  }

  /**
   * Bind window resize event with debouncing
   */
  bindEvents() {
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Handle window resize with debouncing
   */
  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      // Update current device on resize
      const previousDevice = this.currentDevice;
      this.currentDevice = this.getCurrentDevice();
      
      // If device changed, we need to refresh everything
      if (previousDevice !== this.currentDevice) {
        this.refresh();
      } else {
        // Same device, just update heights
        this.updateHeights();
      }
    }, this.options.debounceDelay);
  }

  /**
   * Refresh the height calculations
   * Useful when content changes or new elements are added
   */
  refresh() {
    this.collectElements();
    this.updateHeights();
  }

  /**
   * Add new elements to existing groups or create new groups
   * @param {string} groupName - The group name
   * @param {HTMLElement|NodeList|Array} elements - Element(s) to add
   * @param {string} deviceRestriction - Optional device restriction (e.g., "d,m,t")
   */
  addElements(groupName, elements, deviceRestriction = null) {
    if (!groupName) {
      return;
    }

    // Convert single element or NodeList to array
    const elementsArray = Array.isArray(elements) 
      ? elements 
      : elements instanceof NodeList 
        ? Array.from(elements)
        : [elements];

    // Set the data attributes on elements
    elementsArray.forEach(element => {
      if (element instanceof HTMLElement) {
        element.setAttribute('data-same-height', groupName);
        
        // Set device restriction if provided
        if (deviceRestriction) {
          element.setAttribute('same-height-device', deviceRestriction);
        }
      }
    });

    // Refresh to include new elements
    this.refresh();
  }

  /**
   * Remove elements from height groups
   * @param {HTMLElement|NodeList|Array} elements - Element(s) to remove
   */
  removeElements(elements) {
    // Convert single element or NodeList to array
    const elementsArray = Array.isArray(elements) 
      ? elements 
      : elements instanceof NodeList 
        ? Array.from(elements)
        : [elements];

    elementsArray.forEach(element => {
      if (element instanceof HTMLElement) {
        element.removeAttribute('data-same-height');
        element.removeAttribute('same-height-device');
        element.style.height = 'auto';
      }
    });

    // Refresh to update groups
    this.refresh();
  }

  /**
   * Get all elements in a specific group
   * @param {string} groupName - The group name
   * @returns {Array} Array of elements in the group
   */
  getGroup(groupName) {
    return this.heightGroups.get(groupName) || [];
  }

  /**
   * Get all group names
   * @returns {Array} Array of group names
   */
  getGroupNames() {
    return Array.from(this.heightGroups.keys());
  }

  /**
   * Reset all heights to auto
   */
  resetHeights() {
    this.heightGroups.forEach(elements => {
      elements.forEach(element => {
        element.style.height = 'auto';
      });
    });
  }

  /**
   * Destroy the SameHeight instance and clean up
   */
  destroy() {
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    
    // Clear resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Reset all heights
    this.resetHeights();

    // Clear data structures
    this.heightGroups.clear();
    this.isInitialized = false;
  }

  /**
   * Enable/disable the same height functionality
   * @param {boolean} enabled - Whether to enable same height
   */
  setEnabled(enabled) {
    if (enabled) {
      this.updateHeights();
    } else {
      this.resetHeights();
    }
  }

  /**
   * Get the current device type
   * @returns {string} Current device type ('d', 'm', or 't')
   */
  getDevice() {
    return this.currentDevice;
  }

  /**
   * Get device name from code
   * @param {string} deviceCode - Device code ('d', 'm', 't')
   * @returns {string} Device name
   */
  getDeviceName(deviceCode = this.currentDevice) {
    const deviceNames = {
      'd': 'desktop',
      'm': 'mobile',
      't': 'tablet'
    };
    return deviceNames[deviceCode] || 'unknown';
  }

  /**
   * Update breakpoints and refresh if device changed
   * @param {Object} breakpoints - New breakpoints {mobile: number, tablet: number}
   */
  updateBreakpoints(breakpoints) {
    const previousDevice = this.currentDevice;
    this.options.breakpoints = { ...this.options.breakpoints, ...breakpoints };
    this.currentDevice = this.getCurrentDevice();
    
    if (previousDevice !== this.currentDevice) {
      this.refresh();
    }
  }
}

// Export for use in other modules
export default SameHeight;

// Also make it available globally for direct script inclusion
if (typeof window !== 'undefined') {
  window.SameHeight = SameHeight;
}
